import { useEffect, useState } from "react";
import { Globe2, MapPin } from "lucide-react";
import { IST_TZ, describeInstant, isValidTimeZone } from "../../lib/timeConverter";

// V184 — Live "now" for IST + selected zone. Each tick re-reads Date.now() (no drift).
function useNow() {
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        let t;
        const tick = () => { setNow(Date.now()); t = setTimeout(tick, 1000 - (Date.now() % 1000) + 5); };
        t = setTimeout(tick, 1000 - (Date.now() % 1000) + 5);
        return () => clearTimeout(t);
    }, []);
    return now;
}

const _fmt = new Map();
function clockText(ms, tz, hour12) {
    const key = `${tz}|${hour12}`;
    if (!_fmt.has(key)) {
        _fmt.set(key, new Intl.DateTimeFormat("en-US", { timeZone: tz, hour: "numeric", minute: "2-digit", second: "2-digit", ...(hour12 ? { hour12: true } : { hourCycle: "h23" }) }));
    }
    return _fmt.get(key).format(new Date(ms));
}

function ClockCard({ title, place, tz, now, hour12, accent, testId, icon: Icon }) {
    const d = describeInstant(now, tz, hour12);
    return (
        <div className={`rounded-2xl p-4 sm:p-5 border ${accent ? "bg-[#0F172A] border-[#0F172A] text-white" : "bg-white border-slate-200 text-[#0F172A]"}`} data-testid={testId}>
            <div className="flex items-center justify-between gap-2">
                <span className={`text-[10px] uppercase tracking-[0.2em] font-bold ${accent ? "text-blue-200" : "text-slate-500"}`}>{title}</span>
                <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-emerald-500" data-testid={`${testId}-live`}>
                    <span className="relative flex w-2 h-2"><span className="absolute inline-flex w-full h-full rounded-full bg-emerald-400 opacity-75 animate-ping" /><span className="relative inline-flex w-2 h-2 rounded-full bg-emerald-500" /></span>
                    Live
                </span>
            </div>
            <div className="mt-2 flex items-center gap-1.5 font-semibold text-sm min-w-0">
                <Icon className={`w-4 h-4 shrink-0 ${accent ? "text-amber-400" : "text-[#1E3A8A]"}`} />
                <span className="break-words" data-testid={`${testId}-place`}>{place}</span>
            </div>
            <div className="font-heading font-bold text-3xl sm:text-4xl tabular-nums mt-1 leading-tight" data-testid={`${testId}-time`}>{clockText(now, tz, hour12)}</div>
            <div className={`text-xs mt-1 ${accent ? "text-blue-100" : "text-slate-600"}`} data-testid={`${testId}-date`}>{d.date}</div>
            <div className={`text-[11px] mt-0.5 ${accent ? "text-blue-200" : "text-slate-500"}`} data-testid={`${testId}-offset`}>{d.abbr ? `${d.abbr} · ` : ""}{d.offset} · {tz}</div>
        </div>
    );
}

/** India + selected-location live clocks; display only — never touches converter state. */
export default function LiveClocks({ tz, place, hour12 }) {
    const now = useNow();
    const valid = tz && isValidTimeZone(tz);
    return (
        <div className="grid sm:grid-cols-2 gap-3" data-testid="tc-live-clocks">
            <ClockCard title="India live time (IST)" place="India" tz={IST_TZ} now={now} hour12={hour12} accent icon={Globe2} testId="tc-live-ist" />
            {valid ? (
                <ClockCard title="Selected location live time" place={place} tz={tz} now={now} hour12={hour12} icon={MapPin} testId="tc-live-selected" />
            ) : (
                <div className="rounded-2xl p-4 sm:p-5 border border-dashed border-slate-300 bg-white flex flex-col justify-center" data-testid="tc-live-selected-empty">
                    <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Selected location live time</span>
                    <span className="mt-2 text-sm text-slate-600 inline-flex items-center gap-1.5"><MapPin className="w-4 h-4 text-slate-400" />
                        {tz ? "This time zone isn't recognised — please pick another location." : "Select a country to see its live time."}
                    </span>
                </div>
            )}
        </div>
    );
}
